import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import useWidgetChat from "./useWidgetChat";
import useAutoOpen from "./useAutoOpen";
import Controls from "./Controls";
import PreviewPanel from "./PreviewPanel";
import AiHero from "./AiHero";
import { MessageBubble, TypingBubble } from "./Messages";
import {
  BotIcon, MinimizeIcon, MaximizeIcon, CloseIcon, RefreshIcon, LayoutIcon,
} from "./Icons";

// While the phone keyboard is open, stretch the window to exactly the visible
// area (same as the old widget's "kb-open" behaviour) so it doesn't jump around.
function useKeyboardFillStyle(active) {
  const [style, setStyle] = useState(null);
  useEffect(() => {
    const vv = window.visualViewport;
    if (!active || !vv) return undefined;
    const update = () =>
      setStyle(
        window.innerWidth < 640
          ? { top: vv.offsetTop, left: 0, right: 0, bottom: "auto", height: vv.height, maxHeight: "none", borderRadius: 0 }
          : null
      );
    update();
    vv.addEventListener("resize", update);
    vv.addEventListener("scroll", update);
    return () => {
      vv.removeEventListener("resize", update);
      vv.removeEventListener("scroll", update);
      setStyle(null);
    };
  }, [active]);
  return style;
}

const STYLES = `
  .no-scrollbar::-webkit-scrollbar{display:none}.no-scrollbar{-ms-overflow-style:none;scrollbar-width:none}
  @keyframes tghPop{from{opacity:0;transform:translateY(16px) scale(.92)}to{opacity:1;transform:none}}
  .tgh-window-in{animation:tghPop .28s cubic-bezier(.16,1,.3,1);transform-origin:bottom right}
  @keyframes tghFabIn{from{opacity:0;transform:scale(.4)}to{opacity:1;transform:scale(1)}}
  @keyframes tghPulse{0%{box-shadow:0 0 0 0 rgba(79,70,229,.45)}70%{box-shadow:0 0 0 12px rgba(79,70,229,0)}100%{box-shadow:0 0 0 0 rgba(79,70,229,0)}}
  .tgh-fab{animation:tghFabIn .35s cubic-bezier(.16,1,.3,1),tghPulse 2.6s 1s infinite}
  @media (prefers-reduced-motion:reduce){.tgh-window-in,.tgh-fab{animation:none}}
  /* compact card carousel inside the small floating window (full-screen keeps the big cards) */
  .tgh-widget-small .tgh-gen-container{margin-top:10px !important;padding-top:10px !important}
  .tgh-widget-small .tgh-gen-scroll-row{gap:10px !important;padding:2px 2px 10px !important;scroll-padding-left:2px}
  .tgh-widget-small .tgh-gen-card{flex:0 0 232px !important;width:232px !important;min-width:232px !important;max-width:232px !important}
  .tgh-widget-small .tgh-gen-card:hover{transform:none !important}
  .tgh-widget-small .tgh-gen-card-media,.tgh-widget-small .tgh-gen-card-img{height:104px !important}
`;

/**
 * Floating TGH chatbot.
 * modes: "minimized" (button) | "floating" (corner window) | "fullscreen" (big window, optional split view)
 * Mount once, inside <Router>:  <FloatingChatbot />
 */
export default function FloatingChatbot({ hideOnPaths = [] }) {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const chat = useWidgetChat();

  const [mode, setMode] = useState("minimized");
  const [preview, setPreview] = useState(null); // { type, id, title, url }
  const [chatSide, setChatSide] = useState("left");
  const endRef = useRef(null);
  const dialogRef = useRef(null);
  const [kbOpen, setKbOpen] = useState(false); // phone keyboard open (an input inside the window is focused)

  const hidden =
    new URLSearchParams(search).get("embed") === "true" || // tour page inside the preview iframe
    hideOnPaths.some((p) => pathname.startsWith(p));

  // auto-open (skipped on the full /chatbot page and while a chat is in progress)
  const { markDismissed } = useAutoOpen({
    pathname,
    enabled:
      !hidden && mode === "minimized" && !chat.paymentProcessing && !chat.hasOngoingChat &&
      !pathname.startsWith("/chatbot"),
    onOpen: () => {
      setMode("floating");
      chat.ensureStarted();
    },
  });

  // show the invoice after a payment redirect
  useEffect(() => {
    if (chat.paymentProcessing) setMode("floating");
  }, [chat.paymentProcessing]);

  const openChat = () => {
    markDismissed();
    setMode("floating");
    chat.ensureStarted();
  };
  const closeChat = () => {
    markDismissed();
    setPreview(null);
    setMode("minimized");
  };
  // "Open Page": go to the package page in the SAME tab, and get the widget out of the way
  const goToPage = (url) => {
    setPreview(null);
    setMode(window.innerWidth < 640 ? "minimized" : "floating"); // on phones the window covers the page
    try {
      const u = new URL(url, window.location.origin);
      if (u.origin === window.location.origin) {
        navigate(u.pathname + u.search + u.hash);
        window.scrollTo(0, 0);
      } else {
        window.location.assign(u.href);
      }
    } catch (e) {
      window.location.assign(url);
    }
  };
  // "Click to view in split view": always expand to the full-screen split layout
  // (chat on one side, package preview on the other) on desktop and phones alike.
  const openPreview = (p) => {
    setPreview(p);
    if (mode !== "fullscreen") setMode("fullscreen");
  };

  // lock page scroll behind the widget on phones / fullscreen
  useEffect(() => {
    const lock = mode === "fullscreen" || (mode === "floating" && kbOpen && window.innerWidth < 640);
    if (!lock) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mode, kbOpen]);

  // Scroll to the newest message. For a reply with cards, show its TOP (text + first card)
  // instead of the bottom, so the cards aren't cut off.
  useEffect(() => {
    if (mode === "minimized") return;
    const last = chat.messages[chat.messages.length - 1];
    if (last?.cards && !chat.typing && !chat.loading) {
      const el = dialogRef.current?.querySelector(`[data-msg-id="${last.id}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }
    }
    endRef.current?.scrollIntoView?.({ behavior: "smooth" });
  }, [chat.messages, chat.typing, chat.loading, mode]);

  const vvStyle = useKeyboardFillStyle(mode === "floating" && kbOpen);

  const onFocusIn = (e) => {
    if (window.innerWidth < 640 && e.target.matches?.("input, textarea")) setKbOpen(true);
  };
  const onFocusOut = () => {
    setTimeout(() => {
      const a = document.activeElement;
      if (!a || !dialogRef.current?.contains(a) || !a.matches("input, textarea")) setKbOpen(false);
    }, 100);
  };
  useEffect(() => {
    if (mode !== "floating") setKbOpen(false);
  }, [mode]);

  if (hidden) return null;

  // ---- minimized: just the button ----
  if (mode === "minimized") {
    return (
      <>
        <button
          type="button" onClick={openChat} aria-label="Open chat" id="launcher"
          style={{ padding: 0, border: 0, lineHeight: 0 }}
          className="tgh-fab fixed bottom-[72px] right-5 z-[99999] w-12 h-12 bg-indigo-600 hover:bg-indigo-700 text-white rounded-full shadow-lg transition-transform hover:scale-110 active:scale-95"
        >
          <span className="absolute inset-0 flex items-center justify-center">
            <BotIcon size={22} />
          </span>
          {chat.messages.length === 0 && (
            <span className="absolute -top-0.5 -right-0.5 w-3 h-3 bg-red-500 border-2 border-white rounded-full" />
          )}
        </button>
        <style>{STYLES}</style>
      </>
    );
  }

  const fullscreen = mode === "fullscreen";
  // AI welcome showcase: until the user sends their first AI message
  const aiStart = chat.messages.findIndex((m) => m.kind === "ai");
  const showHero = chat.aiMode && aiStart >= 0 && !chat.messages.slice(aiStart).some((m) => m.role === "user");
  const split = fullscreen && !!preview;
  const overlay = !fullscreen && !!preview; // details shown inside the small widget window

  const containerCls = fullscreen
    ? "inset-0 md:inset-8 flex flex-col md:flex-row md:rounded-2xl overflow-hidden shadow-2xl bg-slate-100"
    : "left-2.5 right-2.5 bottom-4 h-[60vh] max-h-[360px] rounded-xl sm:left-auto sm:right-5 sm:bottom-5 sm:w-[380px] sm:h-[600px] sm:max-h-[85vh] sm:rounded-2xl flex flex-col overflow-hidden shadow-2xl";

  const chatCls = split
    ? `hidden md:flex md:w-[400px] shrink-0 ${chatSide === "right" ? "md:order-last border-l" : "border-r"}`
    : fullscreen
    ? "flex w-full max-w-4xl mx-auto"
    : "flex w-full";

  return (
    <>
      {fullscreen && <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-[99998]" />}

      <div role="dialog" aria-label="TGH travel assistant" ref={dialogRef}
        onFocus={onFocusIn} onBlur={onFocusOut}
        style={!fullscreen ? vvStyle || undefined : undefined}
        className={`tgh-window-in fixed z-[99999] ${fullscreen ? "" : "tgh-widget-small"} ${containerCls}`}>

        {/* ---------- chat column ---------- */}
        <div className={`${chatCls} flex-col h-full min-h-0 bg-slate-50 border-slate-200 relative overflow-hidden`}>

          {/* header */}
          <div className="bg-white border-b border-slate-200 px-4 flex items-center justify-between shrink-0 h-16">
            <div className="flex items-center gap-3 min-w-0">
              <div className="relative shrink-0">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-indigo-700 flex items-center justify-center text-white font-bold shadow-inner">A</div>
                <div className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-500 rounded-full border-2 border-white" />
              </div>
              <div className="min-w-0">
                <h3 className="font-semibold text-slate-800 leading-tight">Aria</h3>
                <p className="text-[11px] text-slate-500 font-medium tracking-wide uppercase truncate">TGH Travel Advisor</p>
              </div>
            </div>

            <div className="flex items-center gap-1 text-slate-400 shrink-0">
              <button type="button" onClick={chat.resetChat} title="New chat" aria-label="New chat"
                className="p-2 hover:bg-slate-100 rounded-lg hover:text-indigo-600 transition-colors">
                <RefreshIcon />
              </button>
              {split && (
                <button type="button" onClick={() => setChatSide((s) => (s === "left" ? "right" : "left"))}
                  title="Swap layout side" aria-label="Swap layout side"
                  className="hidden md:block p-2 hover:bg-slate-100 rounded-lg hover:text-indigo-600 transition-colors">
                  <LayoutIcon />
                </button>
              )}
              <button type="button"
                onClick={() => { if (fullscreen) setPreview(null); setMode(fullscreen ? "floating" : "fullscreen"); }}
                title={fullscreen ? "Minimize to window" : "Expand to full page"}
                aria-label={fullscreen ? "Minimize to window" : "Expand to full page"}
                className="p-2 hover:bg-slate-100 rounded-lg hover:text-indigo-600 transition-colors">
                {fullscreen ? <MinimizeIcon /> : <MaximizeIcon />}
              </button>
              <button type="button" onClick={closeChat} title="Close assistant" aria-label="Close assistant"
                className="p-2 hover:bg-red-50 hover:text-red-500 rounded-lg transition-colors">
                <CloseIcon />
              </button>
            </div>
          </div>

          {/* messages */}
          <div className="flex-1 min-h-0 overflow-y-auto p-4 md:p-6 scroll-smooth bg-slate-50 overscroll-contain">
            {chat.messages.map((m) => (
              <MessageBubble key={m.id} msg={m} onOpenPreview={openPreview} />
            ))}
            {showHero && !chat.busy && <AiHero onPick={chat.sendAi} disabled={chat.busy} wide={fullscreen && !split} />}
            {(chat.typing || chat.loading) && <TypingBubble />}
            <div ref={endRef} />
          </div>

          {/* details panel covering the widget window (small-window mode) */}
          {overlay && (
            <div className="absolute inset-0 z-30 flex flex-col bg-white">
              <PreviewPanel key={`${preview.type}-${preview.id}-${preview.url}`} preview={preview} onClose={() => setPreview(null)} onOpenPage={goToPage} />
            </div>
          )}

          {/* controls */}
          <div className="p-4 bg-white border-t border-slate-100 shrink-0 empty:hidden">
            <Controls chat={chat} />
          </div>
        </div>

        {/* ---------- split view: package preview ---------- */}
        {split && <PreviewPanel key={`${preview.type}-${preview.id}-${preview.url}`} preview={preview} onClose={() => setPreview(null)} onOpenPage={goToPage} />}
      </div>

      <style>{STYLES}</style>
    </>
  );
}